import React, { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { BundleColorSwatch } from '../../components/BundleColorSwatch';
import { BundleProductPrice } from '../../components/BundleProductPrice';
import { BundleCollectionHandle } from '../../enums/CollectionHandle.enum';
import { PageContext } from '../../enums/PageContext.enum';
import { useBundleConfig } from '../../hooks/useBundleConfig';
import { useBundleSelection } from '../../hooks/useBundleSelection';
import { LazyImageLoader } from '../../product_detail/components/LazyImageLoader';
import { pageContextState } from '../../recoil/atoms';
import { isMobileState } from '../../recoil/selectors';
import { Link } from '../../root/components/Link';
import { isDefined } from '../../sizing-tool/utils/array-helper';
import { assetPathFn, getUniqueColorsForBundleConfig } from '../../util';
import styles from './CollectionCard.module.scss';

interface CollectionBundleCardProps {
  collectionHandle: BundleCollectionHandle;
  clearHoverProductHandleDebounce: () => void;
}

export const CollectionBundleCard: React.FC<CollectionBundleCardProps> = ({ collectionHandle, clearHoverProductHandleDebounce }) => {
  const isMobile = useRecoilValue(isMobileState);
  const bundleCollectionConfig = useBundleConfig(collectionHandle);
  const { selectedSwatch, setSelectedSwatch } = useBundleSelection(collectionHandle);

  const swatchNames = useMemo<string[]>(() => bundleCollectionConfig?.productColorCombos.map((pcc) => pcc.swatchTitle) ?? [], [bundleCollectionConfig]);

  const photos = useMemo(() => {
    const selectedCombo = bundleCollectionConfig.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch);
    return [
      selectedCombo ? { src: assetPathFn(selectedCombo.imageAssetUrl) } : undefined,
      ...(bundleCollectionConfig?.productColorCombos
        .find((pcc) => pcc.swatchTitle === selectedSwatch)
        ?.colorCombos.map((cc) => {
          return bundleCollectionConfig.productAssets[cc.productHandle]?.modelPhotos.find((mp) => mp.color === cc.color);
        }) ?? []),
    ].filter(isDefined);
  }, [bundleCollectionConfig.productAssets, bundleCollectionConfig?.productColorCombos, selectedSwatch]);

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const pageContext = useRecoilValue(pageContextState);

  const percentOff = bundleCollectionConfig.percentageDiscount;
  const saleBadgeContents = `Save ${percentOff}%`;

  return (
    <div
      className={styles.collectionCard}
      onMouseEnter={(): void => {
        if (!isMobile) {
          setIsHovering(true);
        }
      }}
      onBlur={() => void 0}
      onFocus={() => void 0}
      onMouseLeave={(): void => {
        if (!isMobile) {
          clearHoverProductHandleDebounce();
          setIsHovering(false);
        }
      }}
    >
      <span className={styles.saleBadge}>{saleBadgeContents}</span>
      <Link
        dataTrackLabel="Collections bundle product image"
        dataTrackLocation={PageContext.CollectionsPage}
        href={`/collections/${collectionHandle}`}
        buttonType="ghost"
      >
        <div style={{ display: 'flex', position: 'relative' }}>
          <div style={{ marginTop: '127%' }}></div>
          <LazyImageLoader
            src={photos[0].src}
            alt={`${bundleCollectionConfig.title}`}
            style={
              isMobile || isHovering
                ? { width: '100%', position: 'absolute', top: 0, left: 0 }
                : { width: '80%', position: 'absolute', top: '10%', left: '10%' }
            }
          />
        </div>
      </Link>
      <div className={styles.quickViewWrapper}>
        <Link
          size="small"
          buttonType="secondary"
          dataTrackLabel="View bundle"
          dataTrackLocation={pageContext}
          text={'View bundle'}
          href={`/collections/${collectionHandle}?bundleColor=${selectedSwatch}`}
        />
      </div>
      <div className={styles.productInfo}>
        <div className={styles.title}>{bundleCollectionConfig.title}</div>
        <BundleProductPrice
          bundleCollectionConfig={bundleCollectionConfig}
          style={
            isMobile
              ? { fontWeight: '400', textAlign: 'center', lineHeight: '20px', fontSize: '12px' }
              : { fontWeight: '400', textAlign: 'center', lineHeight: '28px', fontSize: '16px' }
          }
        />
      </div>
      <div className={styles.colorContainer}>
        {swatchNames.map((swatch, i) => (
          <BundleColorSwatch
            key={i}
            title={swatch}
            colors={getUniqueColorsForBundleConfig(bundleCollectionConfig.productColorCombos, swatch)}
            selected={swatch === selectedSwatch}
            setSelectedSwatch={setSelectedSwatch}
          />
        ))}
      </div>
    </div>
  );
};

export default CollectionBundleCard;
